import { lazy, Suspense } from 'react';
import { useAppSelector } from '@/store/hooks';
import { cn } from '@/lib/utils';
import { WelcomeView } from '@/components/editor/WelcomeView';
import { ContextualToolbar } from '@/components/editor/ContextualToolbar';
import { EmptyState } from '@/components/onboarding/EmptyState';
import { useGetFilesQuery } from '@/store/api/unifiedStorageApi';
import { useDroppable } from '@dnd-kit/core';
import type { File } from '@phrasewright/types';

// Lazy load the heavy Monaco Editor component
const EditorView = lazy(() =>
  import('@/components/editor/EditorView').then((module) => ({
    default: module.EditorView,
  }))
);

interface EditorPanelProps {
  className?: string;
  onStartOnboarding?: () => void;
}

export function EditorPanel({
  className,
  onStartOnboarding,
}: EditorPanelProps) {
  const { selectedFileId } = useAppSelector((state) => state.files);
  const { currentProject } = useAppSelector((state) => state.project);
  const { data: files = [] } = useGetFilesQuery(
    currentProject ? { projectId: currentProject.id } : undefined,
    { skip: !currentProject }
  );

  // Set up drop zone for the editor
  const { isOver, setNodeRef } = useDroppable({
    id: 'editor-drop-zone',
  });

  // Find and convert the selected file
  const selectedStorageFile = selectedFileId
    ? files.find((file) => file.id === selectedFileId)
    : null;

  // Convert to File format if found
  const selectedFile: File | null = selectedStorageFile
    ? {
        id: selectedStorageFile.id,
        name: selectedStorageFile.name,
        path:
          selectedStorageFile.metadata?.path ||
          `/${selectedStorageFile.type}s/${selectedStorageFile.name}`,
        content: selectedStorageFile.content,
        createdAt: selectedStorageFile.createdAt,
        updatedAt: selectedStorageFile.updatedAt,
        metadata: {
          type: selectedStorageFile.type as 'document' | 'phrase',
          title:
            selectedStorageFile.metadata?.title ||
            selectedStorageFile.name.replace(/\.(md|markdown)$/, ''),
          tags: selectedStorageFile.tags || [],
          ...selectedStorageFile.metadata,
        },
        revisions: [],
        status: 'active' as const,
        projectId: selectedStorageFile.projectId,
      }
    : null;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col h-full bg-background relative transition-colors',
        isOver && 'bg-accent/20 border-accent border-2 border-dashed',
        className
      )}
    >
      {!currentProject ? (
        <EmptyState onStartOnboarding={onStartOnboarding} />
      ) : selectedFile ? (
        <>
          <ContextualToolbar file={selectedFile} />
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            }
          >
            <EditorView file={selectedFile} className="flex-1" />
          </Suspense>
        </>
      ) : (
        <WelcomeView />
      )}

      {isOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-accent/10 z-10 pointer-events-none">
          <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
            <p className="text-sm font-medium">Drop to insert file reference</p>
          </div>
        </div>
      )}
    </div>
  );
}
